// Specs of the Reward tab documents (Loyalty, Referral): a log table under a
// summary page that carries the page's chart. Pure (see ./shared.ts).
import { translate } from "shared/languages";
import { formatDateTime, formatMoneyOrDash } from "shared/reports/columnCatalogue";
import { InactiveReason, LoyaltyAnalytics, ReferralAnalytics } from "shared/types";
import { formatCurrency, INACTIVE_REASON_LABELS, UPGRADE_LABEL } from "shared/utils";
import { friendDiscountElsewhere, rewardLabel } from "shared/utils/loyalty";
import { translateWithVars } from "shared/languages";
import { CHART_GAP, CHART_W_HALF, DocSpec, REPORT_PAGE } from "../../pagePlan";
import { joinNotes, taxBasisNote, truncationNote } from "../../reportText";
import { SortMeta } from "../../sort";
import { columns } from "./shared";

// ── The summary pages' own content, shared by the documents and the planner ─

// The stamp chart takes two thirds of the width; the card facts (stamps per
// card, final reward) and the notes sit beside it, where the screen keeps
// them.
export const LOYALTY_CHART_W = Math.round(REPORT_PAGE.textW * 0.66) - CHART_GAP;
const LOYALTY_FACTS_W = REPORT_PAGE.textW - LOYALTY_CHART_W - CHART_GAP;

export const loyaltyFacts = (data: LoyaltyAnalytics): { label: string; value: string }[] => {
  const dist = data.distribution;
  const finalMilestone = dist.milestones.find((m) => m.stamp_number === dist.total_stamps);
  return [
    { label: translate("analyticsPage.loyalty.spec_card_size"), value: String(dist.total_stamps) },
    ...(finalMilestone
      ? [{ label: translate("analyticsPage.loyalty.spec_final_reward"), value: rewardLabel(finalMilestone) }]
      : []),
  ];
};

export const loyaltyHasMilestoneLegend = (data: LoyaltyAnalytics): boolean => {
  const dist = data.distribution;
  const milestoneStamps = new Set(dist.milestones.map((m) => m.stamp_number));
  return dist.buckets.some((b) => milestoneStamps.has(b.stamps));
};

// "12 bulan lalu" / "30 hari lalu" / "sekarang": the retention window's
// bounds, matching the sentence the report page renders under the meter.
// "lalu" / "ago" attaches per bound because "sekarang" is a point in time.
const retentionUnit = (value: number, unit: string) => {
  if (unit === "now") return translate("analyticsPage.referral.unit_now");
  const noun = translate(
    unit === "year"
      ? "analyticsPage.referral.unit_year"
      : unit === "month"
        ? "analyticsPage.referral.unit_month"
        : "analyticsPage.referral.unit_day",
  );
  return translateWithVars("pdf.time_ago", { value, unit: noun });
};

// What the retention meter prints: the rate (null when no referred client
// has matured), its caption and the window note.
export const referralMeter = (data: ReferralAnalytics): { pct: number | null; caption: string; note: string } => {
  const ret = data.retention;
  const pct = ret.matured > 0 ? Math.round((ret.repeat / ret.matured) * 1000) / 10 : null;
  return {
    pct,
    caption:
      pct === null
        ? translate("analyticsPage.referral.retention_empty")
        : translateWithVars("pdf.retention_caption", { repeat: ret.repeat, matured: ret.matured }),
    note: `${translateWithVars("pdf.retention_note", {
      from: retentionUnit(ret.from_value, ret.from_unit),
      to: retentionUnit(ret.to_value, ret.to_unit),
    })}${
      ret.not_yet_matured > 0 ? ` ${ret.not_yet_matured} ${translate("analyticsPage.referral.retention_excluded")}` : ""
    }`,
  };
};

// ── Performa Loyalty Card ───────────────────────────────────────────────────

type LoyaltyRow = LoyaltyAnalytics["table"][number];

const LOYALTY_META = (): SortMeta => ({
  redeemed_at: { label: translate("analyticsPage.columns.claimed_at"), type: "date" },
  stamp_number: { label: translate("analyticsPage.columns.milestone"), type: "number" },
  discount_amount: {
    label: translate("analyticsPage.columns.discount"),
    type: "number",
    // Mirrors the UI sorter: a free credit has no discount value, so it
    // sorts below any real amount rather than tying with Rp 0.
    value: (r: LoyaltyRow) => r.discount_amount ?? -1,
  },
});

export const loyaltySpec: DocSpec<LoyaltyAnalytics, LoyaltyRow> = {
  layout: "summary",
  limited: true,
  meta: LOYALTY_META,
  table: (data) => data.table,
  columns: () =>
    columns<LoyaltyRow>([
      { metric: "claimed_at", render: (r) => formatDateTime(r.redeemed_at) },
      { metric: "client", render: (r) => r.customer_name },
      { metric: "milestone", render: (r) => String(r.stamp_number ?? "-") },
      { metric: "reward", render: (r) => rewardLabel(r) },
      {
        metric: "discount",
        render: (r) => (r.discount_amount === null ? "-" : formatCurrency(r.discount_amount)),
      },
      // A self-claim is stamped as the client's own user, so this prints
      // their name. That is who claimed it.
      { metric: "claimed_by", render: (r) => r.redeemed_by_name ?? "-" },
    ]),
  kpis: (data) => [
    { value: String(data.cards_completed.value), label: translate("analyticsPage.loyalty.kpi_cards_completed") },
    { value: String(data.credits_granted.value), label: translate("analyticsPage.loyalty.kpi_credits_granted") },
    {
      value: formatCurrency(data.discount_value_given.value),
      label: translate("analyticsPage.loyalty.kpi_discount_given"),
    },
  ],
  defaultSort: { key: "redeemed_at", order: "descend" },
  // The chart row: the stamp chart (chart head + chartH) beside the
  // facts column (2pt top padding; per fact an uppercase 6.8pt label, 3pt, a
  // 13pt Inter Tight value, 10pt below; then the 7pt notes), whichever is
  // taller. Mirrors LoyaltyDoc in ../reward.tsx line for line.
  summaryPt: (data, block, m, chartH) => {
    const facts = loyaltyFacts(data).reduce(
      (sum, fact) =>
        sum +
        m.lines(fact.label.toUpperCase(), "Inter-600", 6.8, 0.8, LOYALTY_FACTS_W) * m.lineHeight("Inter-600", 6.8) +
        3 +
        m.lines(fact.value, "InterTight-600", 13, 0, LOYALTY_FACTS_W) * m.lineHeight("InterTight-600", 13) +
        10,
      2,
    );
    const legend = loyaltyHasMilestoneLegend(data)
      ? 2 + m.lines(translate("pdf.milestone_legend"), "Inter-400", 7, 0, LOYALTY_FACTS_W) * m.lineHeight("Inter-400", 7)
      : 0;
    const excluded =
      data.distribution.excluded_old_config > 0
        ? 4 +
          m.lines(
            translateWithVars("pdf.old_structure_note", { n: data.distribution.excluded_old_config }),
            "Inter-400",
            7,
            0,
            LOYALTY_FACTS_W,
          ) *
            m.lineHeight("Inter-400", 7)
        : 0;
    return 18 + Math.max(block.chartHead + chartH, facts + legend + excluded);
  },
  // The basis line follows the truncation note when a claim was spent on a
  // taxed sale: every discount figure here is before tax.
  note: (data, shown, loaded, sortedOffDefault) =>
    joinNotes(
      truncationNote({
        shown,
        total: data.table_total,
        noun: translate("pdf.noun.klaim"),
        loaded,
        sortedOffDefault,
      }),
      taxBasisNote(data, "discounts"),
    ),
};

// ── Performa Referral ───────────────────────────────────────────────────────

type ReferralRow = ReferralAnalytics["table"][number];

const REFERRAL_META = (): SortMeta => ({
  converted_at: { label: translate("analyticsPage.columns.converted_at"), type: "date" },
  first_purchase_value: { label: translate("analyticsPage.columns.purchase_value"), type: "number" },
});

// Under the converting product, the same word Pembelian prints: Voided (the
// sale never happened, its value is a dash) or Upgrade (its value is the fee).
const conversionStatusWord = (r: ReferralRow) =>
  r.is_voided
    ? INACTIVE_REASON_LABELS[InactiveReason.MANUALLY_MARKED_INVALID]
    : r.is_upgrade === true
      ? UPGRADE_LABEL
      : null;

const referrerReward = (r: ReferralRow) =>
  r.referrer_reward_type
    ? rewardLabel({
        type: r.referrer_reward_type,
        credit_qty: r.referrer_credit_qty,
        discount_mode: r.referrer_discount_mode,
        discount_value: r.referrer_discount_value,
      })
    : "-";

// The friend's reward as configured, since a free credit has no rupiah
// amount to print.
const friendReward = (r: ReferralRow) =>
  r.friend_reward_type
    ? rewardLabel({
        type: r.friend_reward_type,
        credit_qty: r.friend_credit_qty,
        discount_mode: r.friend_discount_mode,
        discount_value: r.friend_discount_value,
      })
    : "-";

export const referralSpec: DocSpec<ReferralAnalytics, ReferralRow> = {
  layout: "summary",
  limited: true,
  meta: REFERRAL_META,
  table: (data) => data.table,
  columns: (data) =>
    columns<ReferralRow>([
      { metric: "converted_at", render: (r) => formatDateTime(r.converted_at) },
      { metric: "referrer", render: (r) => r.referrer_name },
      { metric: "new_client", render: (r) => r.referred_name },
      { metric: "referrer_reward", render: referrerReward },
      // Under it, the later sale the discount went on when it was not the
      // converting one (R20), as on the screen.
      { metric: "new_client_reward", render: friendReward, sub: friendDiscountElsewhere },
      // The purchase that CONVERTED the referral: neither necessarily the
      // client's first nor the one the reward was spent on.
      { metric: "conversion_product", render: (r) => r.package_name ?? "-", sub: conversionStatusWord },
      // Pembatasan Admin: dropped for a financially restricted Admin, exactly
      // as the screen drops it. The reward columns above stay: what the
      // programme cost is not revenue.
      ...(data.money_hidden
        ? []
        : [{
            metric: "purchase_value" as const,
            render: (r: ReferralRow) => (r.is_voided ? "-" : formatMoneyOrDash(r.first_purchase_value)),
          }]),
    ]),
  kpis: (data) => {
    const costPerClient = data.discount_cost_per_client.value;
    return [
      { value: String(data.new_clients.value), label: translate("analyticsPage.referral.kpi_new_clients") },
      // Dropped with the Nilai Pembelian column, never alone.
      ...(data.money_hidden
        ? []
        : [{
            value: formatCurrency(data.first_purchase_revenue?.value ?? 0),
            label: translate("analyticsPage.referral.kpi_first_purchase_revenue"),
          }]),
      {
        value: costPerClient === null ? "-" : formatCurrency(costPerClient),
        label: translate("analyticsPage.referral.kpi_cost_per_client"),
      },
    ];
  },
  defaultSort: { key: "converted_at", order: "descend" },
  // The chart row: the trend chart (chart head + chartH) beside the
  // retention meter (chart head; then either the 8pt empty caption, or the
  // 20pt Inter Tight rate, 6pt, the 10pt bar, 6pt and the 7.5pt caption;
  // then the 7pt note 4pt below), whichever is taller. Mirrors PdfMeter and
  // ReferralDoc line for line.
  summaryPt: (data, block, m, chartH) => {
    const meter = referralMeter(data);
    const body =
      meter.pct === null
        ? m.lines(meter.caption, "Inter-400", 8, 0, CHART_W_HALF) * m.lineHeight("Inter-400", 8)
        : m.lineHeight("InterTight-600", 20) +
          6 +
          10 +
          6 +
          m.lines(meter.caption, "Inter-400", 7.5, 0, CHART_W_HALF) * m.lineHeight("Inter-400", 7.5);
    const note = 4 + m.lines(meter.note, "Inter-400", 7, 0, CHART_W_HALF) * m.lineHeight("Inter-400", 7);
    return 18 + Math.max(block.chartHead + chartH, block.chartHead + body + note);
  },
  note: (data, shown, loaded, sortedOffDefault) =>
    joinNotes(
      truncationNote({
        shown,
        total: data.table_total,
        noun: translate("pdf.noun.konversi"),
        loaded,
        sortedOffDefault,
      }),
      taxBasisNote(data, "discounts"),
    ),
};
